import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { holdReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * `extractions`: the AI reading of a document. Server-only rows that clients only pull
 * (receipts plan 2.2); a push of an extraction is rejected, so there is no request schema.
 */

export const extractionStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
  "skipped_quota",
])
export type ExtractionStatus = z.infer<typeof extractionStatusSchema>

export const extractionErrorCodeSchema = z.enum([
  "provider_timeout",
  "provider_error",
  "unreadable",
  "consent_missing",
])
export type ExtractionErrorCode = z.infer<typeof extractionErrorCodeSchema>

/** Fields whose provider disagreement forces a manual review (receipts plan 2.2). */
export const EXTRACTION_DISAGREEMENT_KEYS = ["total_minor", "issued_on", "abn"] as const

/** A field with a confidence below this goes to the review inbox. */
export const EXTRACTION_REVIEW_CONFIDENCE_MIN = 0.8
/** Overall confidence below this makes the orchestrator try the backup provider. */
export const EXTRACTION_FALLBACK_CONFIDENCE_MIN = 0.6

const fieldOf = <T extends z.ZodType>(value: T, confidence: z.ZodNumber) =>
  z.object({ value: value.nullable(), confidence })

/**
 * Shape of `extractions.fields`, shared by the three platforms through test vectors. Every
 * field is optional: a provider may not find it. Confidence is 0..1 for provider
 * output and unbounded for rows already stored (lenient).
 */
function buildFieldsSchema(confidence: z.ZodNumber) {
  return z.object({
    vendor_name: fieldOf(z.string(), confidence).optional(),
    abn: fieldOf(z.string(), confidence).optional(),
    invoice_number: fieldOf(z.string(), confidence).optional(),
    issued_on: fieldOf(z.string(), confidence).optional(),
    total_minor: fieldOf(z.number().int(), confidence).optional(),
    gst_minor: fieldOf(z.number().int(), confidence).optional(),
    currency: fieldOf(z.string(), confidence).optional(),
    is_tax_invoice: fieldOf(z.boolean(), confidence).optional(),
    suggested_category: fieldOf(z.string(), confidence).optional(),
    /** Line items; the source of the later `expense_lines`. */
    lines: z
      .array(z.object({ description: z.string(), amount_minor: z.number().int(), confidence }))
      .optional(),
  })
}

/** Stored/response shape (lenient). */
export const extractionFieldsSchema = buildFieldsSchema(z.number())
export type ExtractionFields = z.infer<typeof extractionFieldsSchema>

/** What an extraction provider must return (confidence within 0..1). */
export const extractionProviderOutputSchema = buildFieldsSchema(z.number().min(0).max(1))

export const extractionSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  document_id: z.uuid(),
  status: extractionStatusSchema,
  attempt: z.number().int().nonnegative(),
  provider: z.string(),
  model_version: z.string().nullable().default(null),
  fields: extractionFieldsSchema.default({}),
  disagreement_fields: z.array(z.string()).default([]),
  // Lenient: plain text in the database.
  error_code: z.string().nullable().default(null),
  /** Set when an AI-read quota unit is reserved; cleared on failure. `aiReads.used` counts it. */
  counted_at: z.string().datetime({ offset: true }).nullable().default(null),
  started_at: z.string().datetime({ offset: true }).nullable().default(null),
  finished_at: z.string().datetime({ offset: true }).nullable().default(null),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: holdReasonSchema.nullable().default(null),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Extraction = z.infer<typeof extractionSchema>
